"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BottomNav } from "@/components/bottom-nav";
import {
  ArrowLeft,
  Loader2,
  Plus,
  Trash2,
  UserPlus,
  Phone,
  Mail,
  Briefcase,
  User,
  Save,
} from "lucide-react";
import toast from "react-hot-toast";

interface Contact {
  id?: string;
  name: string;
  role: string;
  phone: string;
  email: string;
  is_primary: boolean;
  _isNew?: boolean;
}

export default function AdminContactsPage() {
  const router = useRouter();
  const supabase = createClient();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);

  useEffect(() => {
    async function load() {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        router.push("/auth/login");
        return;
      }

      // Get company
      const { data: company } = await supabase
        .from("companies")
        .select("id")
        .eq("profile_id", user.id)
        .single();

      if (!company) {
        toast.error("No company profile found");
        router.push("/contractor/profile");
        return;
      }

      setCompanyId(company.id);

      // Get existing contacts
      const { data: existingContacts } = await supabase
        .from("company_contacts")
        .select("*")
        .eq("company_id", company.id)
        .order("is_primary", { ascending: false })
        .order("created_at", { ascending: true });

      if (existingContacts && existingContacts.length > 0) {
        setContacts(existingContacts);
      }

      setLoading(false);
    }
    load();
  }, [supabase, router]);

  function addContact() {
    setContacts([
      ...contacts,
      {
        name: "",
        role: "",
        phone: "",
        email: "",
        is_primary: contacts.length === 0,
        _isNew: true,
      },
    ]);
  }

  function removeContact(index: number) {
    setContacts(contacts.filter((_, i) => i !== index));
  }

  function updateContact(index: number, field: keyof Contact, value: string | boolean) {
    const updated = [...contacts];
    (updated[index] as unknown as Record<string, unknown>)[field] = value;

    // If setting as primary, unset others
    if (field === "is_primary" && value === true) {
      updated.forEach((c, i) => {
        if (i !== index) c.is_primary = false;
      });
    }

    setContacts(updated);
  }

  async function handleSave() {
    // Validate
    const hasEmpty = contacts.some((c) => !c.name.trim());
    if (hasEmpty) {
      toast.error("All contacts need at least a name");
      return;
    }

    if (!companyId) return;
    setSaving(true);

    try {
      // Delete all existing contacts for this company and re-insert
      await supabase.from("company_contacts").delete().eq("company_id", companyId);

      if (contacts.length > 0) {
        const toInsert = contacts.map((c) => ({
          company_id: companyId,
          name: c.name.trim(),
          role: c.role.trim() || null,
          phone: c.phone.trim() || null,
          email: c.email.trim() || null,
          is_primary: c.is_primary,
        }));

        const { error } = await supabase.from("company_contacts").insert(toInsert);

        if (error) {
          toast.error(`Save failed: ${error.message}`);
          setSaving(false);
          return;
        }
      }

      toast.success("Contacts saved!");
      router.push("/contractor/profile");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-screen-sm px-4 py-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <Button variant="ghost" size="icon" onClick={() => router.back()}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold">Team Contacts</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-6 ml-12">
          Add site supervisors, admin staff, or anyone workers might need to contact.
        </p>

        {/* Contacts List */}
        <div className="space-y-4">
          {contacts.map((contact, index) => (
            <div
              key={contact.id || `new-${index}`}
              className="rounded-lg border bg-card p-4 space-y-3"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">
                  Contact {index + 1}
                  {contact.is_primary && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-brand-orange/10 px-2 py-0.5 text-xs font-medium text-brand-orange">
                      Primary
                    </span>
                  )}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => removeContact(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Name *</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={contact.name}
                      onChange={(e) => updateContact(index, "name", e.target.value)}
                      placeholder="e.g. Dave Wilson"
                      className="h-11 text-base pl-10"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm">Role</Label>
                  <div className="relative mt-1">
                    <Briefcase className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={contact.role}
                      onChange={(e) => updateContact(index, "role", e.target.value)}
                      placeholder="e.g. Site Supervisor, Admin, Accounts"
                      className="h-11 text-base pl-10"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Phone</Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="tel"
                        value={contact.phone}
                        onChange={(e) => updateContact(index, "phone", e.target.value)}
                        placeholder="0400 000 000"
                        className="h-11 text-base pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Email</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="email"
                        value={contact.email}
                        onChange={(e) => updateContact(index, "email", e.target.value)}
                        placeholder="dave@example.com"
                        className="h-11 text-base pl-10"
                      />
                    </div>
                  </div>
                </div>

                {!contact.is_primary && (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => updateContact(index, "is_primary", true)}
                  >
                    Set as primary contact
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Add Contact Button */}
          <Button
            variant="outline"
            className="w-full h-12 text-base border-dashed"
            onClick={addContact}
          >
            <Plus className="mr-2 h-5 w-5" />
            Add Contact
          </Button>

          {contacts.length === 0 && (
            <div className="text-center py-8 space-y-3">
              <UserPlus className="h-12 w-12 mx-auto text-muted-foreground/40" />
              <div>
                <p className="font-medium">No team contacts yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Add people workers can contact about jobs — site supers, admin staff, accounts.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="fixed bottom-16 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur pb-[env(safe-area-inset-bottom)]">
          <div className="mx-auto max-w-screen-sm px-4 py-3">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full h-14 text-lg font-semibold"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-5 w-5" />
                  Save Contacts
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      <BottomNav userType="contractor" />
    </div>
  );
}
